import {useContext, useEffect, useState} from "react"
import {useSearchParams} from "react-router-dom"
import useSWR from "swr"
import {default as cx} from "classnames"

import PickOrientee from "../../components/surveys/osr/PickOrientee"
import SavedOSRs from "../../components/surveys/osr/SavedOSRs"
import OSRHeader from "../../components/surveys/osr/OSRHeader"
import DeleteSurveyModal from "../../components/surveys/DeleteSurveyModal"
import {wrappedGet, wrappedPost, wrappedDelete} from "../../utilities/AxiosHelper.jsx"
import {fetcherGet, normalizeDate, formatDateForApi, ORIENTATION_STATUS} from "../../utilities/general.jsx"
import RefContext from "../../utilities/RefContext.jsx"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

const OrientationStatusReportSelection = () => {
    useEffect(() => {
        document.title = "Orientation Status Report Selection"
    }, [])
    const [searchParams] = useSearchParams()
    const [orienteeName, setOrienteeName] = useState("")
    const [shiftDate, setShiftDate] = useState(normalizeDate(new Date()))
    const [orientationId, setOrientationId] = useState(searchParams.get("orientationId"))
    const initialTab = searchParams.get("tab")
    const [activeTab, setActiveTab] = useState(initialTab || "new")
    const [draftToDelete, setDraftToDelete] = useState(null)
    const orientationWeekId = searchParams.get("orientationWeekId")
    const guestPreceptor = searchParams.get("guestPreceptor")
    const isGuest = Boolean(guestPreceptor || orientationId === null) 
    useEffect(() => {
        if (isGuest) {
            fetchGuestInformation()
        } else {
            fetchOrientationInformation(orientationId)
        }

    }, [orientationId, orientationWeekId, isGuest, activeTab])

    const [orientations, setOrientations] = useState([])
    const [orientationWeeks, setOrientationWeeks] = useState([])
    const [orientationList, setOrientationList] = useState([])
    const [isLoading, setIsLoading] = useState(false);


    const {dispatch} = useContext(RefContext)
    const navigate = useStableNavigate()

    const {data: drafts, mutate: mutateDrafts} = useSWR("/surveys/get-incomplete-responses/osr", fetcherGet, {
        fallbackData: [],
    })

    const fetchGuestInformation = async () => {
        setIsLoading(true)
        const lookupOrientations = await wrappedGet("/list-orientations");
        setOrientations(lookupOrientations?.data?.results)
        setOrientationList(lookupOrientations?.data?.results)
        getOrientationWeeks()
    }

    const getOrientationWeeks = async () => {
        if (orientationId) {
            const lookupOrientationWeeks = await wrappedGet(`/educators/orientations/${orientationId}/weeks`);
            const orientationWeeks = lookupOrientationWeeks?.data?.results;
            setOrientationWeeks(orientationWeeks)
        }
        setIsLoading(false)
    }

    const fetchOrientationInformation = async (orientationId) => {
        if (orientationId) {
            setIsLoading(true)
            const lookupOrientation = await wrappedGet(`/orientees/${orientationId}`);
            const data = lookupOrientation?.data?.results;
            setOrienteeName(`${data?.name}`)
            getOrientationWeeks()
        }
    }

    const isPaused =
        drafts.length > 0 && drafts.every((draft) => draft.orientation_status_id === ORIENTATION_STATUS.PAUSED)

    const handleDeleteConfirm = () => {
        wrappedDelete(`/surveys/start-response/osr/${draftToDelete.id}`)
            .then(() => {
                mutateDrafts()
                setDraftToDelete(null)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Could not delete draft. Please try again.",
                })
            })
    }

    const handleResume = (draft) => {
        navigate(`/surveys/orientation-status-report/${draft.id}`)
    }

    const startReport = () => {
        if (!orientationId) return

        if (!shiftDate) {
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: "Shift Date is required",
                timeout: 5000,
            })
            return
        }

        const parsedOrientationWeekId = orientationWeekId ? Number.parseInt(orientationWeekId, 10) : null
        wrappedPost(`/surveys/start-response/osr/${orientationId}`, {
            shift_date: formatDateForApi(shiftDate),
            orientation_week_id: Number.isNaN(parsedOrientationWeekId) ? null : parsedOrientationWeekId,
        })
            .then((res) => {
                navigate(`/surveys/orientation-status-report/${res.data.response_id}`)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "An error occurred starting the report. Please try again.",
                })
            })
    }

    return (
        <>
            <OSRHeader orienteeName={orienteeName} shiftDate={shiftDate ? new Date(shiftDate) : null} />
            <div className="container mw-800px px-md-0">
                <div className="btn-group w-100 my-5" role="tablist">
                    <button
                        className={cx("btn btn-light-primary btn-active-primary fw-bold border border-primary", {
                            active: activeTab === "new",
                        })}
                        disabled={isPaused}
                        onClick={() => setActiveTab("new")}
                    >
                        Create New OSR
                    </button>
                    <button
                        className={cx("btn btn-light-primary btn-active-primary fw-bold border border-primary", {
                            active: activeTab === "saved",
                        })}
                        disabled={isPaused}
                        onClick={() => setActiveTab("saved")}
                    >
                        Saved OSRs
                        {drafts.length > 0 && (
                            <span className="badge badge-square badge-danger shadow-sm ms-2 px-3">{drafts.length}</span>
                        )}
                    </button>
                </div>

                {activeTab === "new" && isPaused === false && (
                    <div className="card card-flush border mb-4">
                        <div className="card-body pt-6">
                            <form className="pt-0 w-100 px-0">
                                <PickOrientee
                                    orientationId={orientationId}
                                    orientationWeekId={orientationWeekId}
                                    orientationList={orientationList}
                                    setOrientationId={setOrientationId}
                                    orienteeName={orienteeName}
                                    orientationWeeks={orientationWeeks}
                                    shiftDate={shiftDate}
                                    setShiftDate={setShiftDate}
                                    startReport={startReport}
                                    isLoading={isLoading}
                                    isGuest={isGuest}
                                />
                            </form>
                        </div>
                    </div>
                )}

                {activeTab === "saved" && (
                    <div className="card card-flush">
                        <div className="card-header">
                            <h3 className="card-title fw-bold fs-2">Saved OSRs</h3>
                        </div>
                        <div className="card-body pt-0">
                            <SavedOSRs
                                drafts={drafts}
                                onResume={handleResume}
                                onDelete={(draft) => setDraftToDelete(draft)}
                            />
                        </div>
                    </div>
                )}
            </div>

            <DeleteSurveyModal
                draft={draftToDelete}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDraftToDelete(null)}
                deleteMessage="You have chosen to delete this OSR. Please confirm your choice or cancel this action to
                            return to page."
            />
        </>
    )
}

export default OrientationStatusReportSelection
